import { skipToken } from "@reduxjs/toolkit/query";
import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { type WorkRecordReach, useGetWorkRecordReachQuery } from "permissions/_api/work-record-reach-api-service";
import { type GrantRowModel, resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import { type GrantLevel, type ReachCount, kindReach } from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import { ResourceType } from "permissions/resources/permission-resource-types";

/**
 * What each standing kind covers for one person, as the person page's rows draw it (#6831).
 *
 * The clauses come with the kinds, from `GET /permissions/kinds`; the count of proposals and work
 * agreements a Need-to-know grant covers comes from `GET /work/reach/{userId}`, which counts what the
 * by-id reads would open for them. Only those two kinds are counted: the other kinds' records are
 * not read per person by any route yet, so their rows give the scope's clause alone.
 */

/** The kinds whose Need-to-know reach is counted, and where in the Work answer each count is. */
const COUNTED_KINDS: Readonly<Partial<Record<string, Readonly<{ noun: string; count: (reach: WorkRecordReach) => number }>>>> = {
  [ResourceType.ProposalsAccess]: { noun: "proposal", count: (reach) => reach.proposals },
  [ResourceType.WorkAgreementsAccess]: { noun: "work agreement", count: (reach) => reach.workAgreements },
};

/**
 * The level a row's kind is held at for the reach line, or undefined where it is not in force: not
 * granted, or removed. Everybody holds notifications at need-to-know, so its not-granted row is held.
 */
export function heldLevel(model: GrantRowModel, permission: Permission | undefined): GrantLevel | undefined {
  if (model.state === "enabled" && permission) return resourceLevel(permission.resource);
  if (model.state === "not-granted" && model.lock?.reason === "inherent") return model.level;
  return undefined;
}

export function reachCountFor(kind: PermissionKind, reach: WorkRecordReach | undefined): ReachCount | undefined {
  const counted = COUNTED_KINDS[kind.resourceType];
  if (!counted || !reach) return undefined;
  return { noun: counted.noun, count: counted.count(reach) };
}

/**
 * The reach lines of every row on one person's page. The count is asked only of a viewer who may read
 * member permissions, which is what the Work route asks; without it, or while it loads, a row says
 * what its scope covers without the count.
 */
export default function usePersonKindReach(
  userId: Guid,
  options: Readonly<{ mayReadMemberPermissions: boolean; mayAdministerPlatform: boolean }>,
): (model: GrantRowModel, permission: Permission | undefined) => string[] {
  const { data } = useGetWorkRecordReachQuery(options.mayReadMemberPermissions ? userId.value : skipToken);
  return (model, permission) =>
    kindReach(model.kind, heldLevel(model, permission), {
      mayAdministerPlatform: options.mayAdministerPlatform,
      count: reachCountFor(model.kind, data),
    });
}
